import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faTimesCircle,
  faCircle,
  faChevronDown,
  faChevronRight,
  faInfoCircle,
  faFileAlt,
  faPen
} from "@fortawesome/free-solid-svg-icons";

export default function ContractorCompliance(props) {

  var onEditItem = props.onEditItem;
  var onOpenDocument = props.onOpenDocument;

  var sections = useMemo(function () {
    return buildContractorComplianceData();
  }, []);

  var [expandedMap, setExpandedMap] = useState(
    buildInitialExpandedMap(sections)
  );

  var [hoveredInfoKey, setHoveredInfoKey] = useState(null);

  useEffect(function () {
    setExpandedMap(buildInitialExpandedMap(sections));
  }, [sections]);

  function buildInitialExpandedMap(list) {
    var map = {};
    var i;
    for (i = 0; i < list.length; i++) {
      map[list[i].id] = true;
    }
    return map;
  }

  function toggleSection(id) {
    setExpandedMap(function (prev) {
      var next = {};
      var k;
      for (k in prev) next[k] = prev[k];
      next[id] = !prev[id];
      return next;
    });
  }

  function getSectionRag(section) {
    var hasFail = false;
    var hasAmber = false;

    section.items.forEach(function (i) {
      if (i.status === "fail") hasFail = true;
      if (i.status === "conditional" || i.status === "expiring") hasAmber = true;
    });

    if (hasFail) return "red";
    if (hasAmber) return "amber";
    return "green";
  }

  function getNonComplianceCount(section) {
    var c = 0;
    section.items.forEach(function (i) {
      if (i.status === "fail") c++;
    });
    return c;
  }

  function getRagDotStyle(rag) {
    var s = {
      width: "10px",
      height: "10px",
      borderRadius: "50%",
      border: "1px solid #333",
      marginRight: "8px"
    };

    if (rag === "red") s.background = "#e74c3c";
    else if (rag === "amber") s.background = "#f1c40f";
    else s.background = "#2ecc71";

    return s;
  }

  function getStatusIcon(item) {
    if (item.status === "pass") return <FontAwesomeIcon icon={faCheckCircle} />;
    if (item.status === "fail") return <FontAwesomeIcon icon={faTimesCircle} />;
    return <FontAwesomeIcon icon={faCircle} />;
  }

  function getStatusColor(item) {
    if (item.status === "pass") return "#2ecc71";
    if (item.status === "fail") return "#e74c3c";
    if (item.status === "expiring") return "#f39c12";
    if (item.status === "conditional") return "#f1c40f";
    if (item.status === "na") return "#3498db";
    return "#bbb";
  }

  function renderSection(section) {
    var expanded = expandedMap[section.id];
    var rag = getSectionRag(section);

    return (
      <div key={section.id} style={styles.sectionWrapper}>
        <div
          style={styles.sectionHeader}
          onClick={function () {
            toggleSection(section.id);
          }}
        >
          <div style={styles.sectionHeaderLeft}>
            <div style={styles.chevron}>
              <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
            </div>

            <span style={getRagDotStyle(rag)} />
            <div style={styles.sectionTitle}>{section.title}</div>
          </div>

          <div style={styles.nonCompBadge}>
            {getNonComplianceCount(section)} Non-Compliant
          </div>
        </div>

        {expanded && renderTable(section)}
      </div>
    );
  }

  function renderTable(section) {
    return (
      <div style={styles.tableWrapper}>
        <div style={styles.tableHeaderRow}>
          <div style={styles.colStatus}>Status</div>
          <div style={styles.colName}>Requirement</div>
          <div style={styles.colDate}>Checked</div>
          <div style={styles.colBy}>Checked By</div>
          <div style={styles.colInfo}>Info</div>
          <div style={styles.colDoc}>Doc</div>
          <div style={styles.colEdit}>Edit</div>
        </div>

        {section.items.map(function (item) {
          var key = section.id + ":" + item.id;
          var showTooltip = hoveredInfoKey === key;

          return (
            <div key={item.id} style={styles.tableRow}>
              <div style={styles.colStatus}>
                <span style={{ color: getStatusColor(item) }}>
                  {getStatusIcon(item)}
                </span>
              </div>

              <div style={styles.colName}>{item.name}</div>
              <div style={styles.colDate}>{item.testedOn || "—"}</div>
              <div style={styles.colBy}>{item.testedBy || "—"}</div>

              <div style={styles.colInfo}>
                <div
                  style={styles.iconButton}
                  onMouseEnter={function () {
                    setHoveredInfoKey(key);
                  }}
                  onMouseLeave={function () {
                    setHoveredInfoKey(null);
                  }}
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                  {showTooltip && (
                    <div style={styles.tooltip}>
                      <div style={styles.tooltipTitle}>Notes</div>
                      <div style={styles.tooltipBody}>
                        {item.comments || "No comments recorded."}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div style={styles.colDoc}>
                {item.documentName ? (
                  <div
                    style={styles.iconButton}
                    onClick={function () {
                      onOpenDocument && onOpenDocument(section, item);
                    }}
                  >
                    <FontAwesomeIcon icon={faFileAlt} />
                  </div>
                ) : (
                  <span style={styles.docMissing}>—</span>
                )}
              </div>

              <div style={styles.colEdit}>
                <div
                  style={styles.editButton}
                  onClick={function () {
                    onEditItem && onEditItem(section, item);
                  }}
                >
                  <FontAwesomeIcon icon={faPen} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={styles.panelOuter}>
      <div style={styles.panelHeader}>
        <div style={styles.panelTitle}>Contractor Compliance</div>
        <div style={styles.panelSubtitle}>
          Insurance, accreditations & mandatory requirements
        </div>
      </div>

      <div style={styles.sectionsArea}>
        {sections.map(function (s) {
          return renderSection(s);
        })}
      </div>
    </div>
  );
}

/* ---------------------------
   Contractor Demo Data
---------------------------- */

function buildContractorComplianceData() {
  return [
    {
      id: "insurance",
      title: "Insurance",
      items: [
        makeItem("pli", "Public Liability Insurance", "pass", "12 Jun 2025", "Broker", "£10m cover in place.", "PLI.pdf"),
        makeItem("eli", "Employers Liability Insurance", "expiring", "01 Feb 2025", "Broker", "Renewal due within 30 days.", "ELI.pdf")
      ]
    },
    {
      id: "accreditation",
      title: "Accreditations",
      items: [
        makeItem("chas", "CHAS Accreditation", "pass", "21 Jan 2025", "CHAS", "Accreditation valid.", "CHAS.pdf"),
        makeItem("safe", "SafeContractor", "fail", "14 Nov 2024", "Audit", "Expired – renewal required.", "SafeContractor.pdf"),
        makeItem("iso", "ISO 9001", "pass", "02 Aug 2025", "Auditor", "Quality management certified.", "ISO9001.pdf")
      ]
    },
    {
      id: "policy",
      title: "Policies & Inductions",
      items: [
        makeItem("hs", "Health & Safety Policy", "pass", "05 Mar 2025", "Contractor", "Current policy on file.", "HS_Policy.pdf"),
        makeItem("ind", "Contractor Induction Completed", "conditional", "10 Dec 2025", "Security", "New operatives pending induction.", "")
      ]
    }
  ];
}

function makeItem(id, name, status, testedOn, testedBy, comments, documentName) {
  return {
    id,
    name,
    status,
    testedOn,
    testedBy,
    comments,
    documentName
  };
}

/* ---------------------------
   Styles
---------------------------- */

var styles = {
  panelOuter: {
    width: "100%",
    height: "100%",
    background: "#141414",
    border: "1px solid #2a2a2a",
    borderRadius: "10px",
    overflow: "hidden",
    fontFamily: "Arial, sans-serif"
  },

  panelHeader: {
    padding: "14px 14px 10px 14px",
    borderBottom: "1px solid #2a2a2a",
    background: "#171717"
  },

  panelTitle: {
    fontSize: "14px",
    fontWeight: "bold",
    color: "#fff"
  },

  panelSubtitle: {
    marginTop: "4px",
    fontSize: "11px",
    color: "#9a9a9a"
  },

  sectionsArea: {
    padding: "12px"
  },

  sectionWrapper: {
    marginBottom: "10px",
    border: "1px solid #2a2a2a",
    borderRadius: "10px",
    overflow: "hidden",
    background: "#161616"
  },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 12px",
    cursor: "pointer",
    background: "#1a1a1a"
  },

  sectionHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: "10px"
  },

  chevron: {
    width: "18px",
    color: "#bbb"
  },

  sectionIcon: {
    display: "flex",
    alignItems: "center"
  },

  sectionTitle: {
    color: "#fff",
    fontSize: "12px",
    fontWeight: "bold"
  },

  sectionHeaderRight: {
    display: "flex",
    alignItems: "center",
    gap: "10px"
  },

  nonCompBadge: {
    fontSize: "11px",
    color: "#ddd",
    border: "1px solid #2f2f2f",
    background: "#151515",
    padding: "4px 10px",
    borderRadius: "999px"
  },

  tableWrapper: {
    padding: "10px 10px 6px 10px"
  },

  tableHeaderRow: {
    display: "flex",
    alignItems: "center",
    padding: "8px 8px",
    border: "1px solid #242424",
    borderRadius: "8px",
    background: "#121212",
    color: "#9a9a9a",
    fontSize: "10px",
    marginBottom: "8px"
  },

  tableRow: {
    display: "flex",
    alignItems: "center",
    padding: "10px 8px",
    borderTop: "1px solid #202020",
    color: "#ddd",
    fontSize: "11px"
  },

  colStatus: { width: "60px", display: "flex", alignItems: "center" },
  colName: { flex: 1, minWidth: "220px" },
  colDate: { width: "110px" },
  colBy: { width: "140px" },
  colInfo: { width: "70px", position: "relative" },
  colDoc: { width: "60px", display: "flex", justifyContent: "center" },
  colEdit: { width: "60px", display: "flex", justifyContent: "flex-end" },

  itemName: {
    color: "#fff",
    fontSize: "12px"
  },

  iconButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    borderRadius: "8px",
    border: "1px solid #2a2a2a",
    background: "#151515",
    color: "#bbb",
    cursor: "pointer",
    position: "relative"
  },

  docMissing: {
    color: "#666",
    fontSize: "12px"
  },

  editButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "28px",
    borderRadius: "8px",
    border: "1px solid #2a2a2a",
    background: "#151515",
    color: "#ddd",
    cursor: "pointer"
  },

  tooltip: {
    position: "absolute",
    right: "0px",
    top: "34px",
    width: "260px",
    background: "#0f0f0f",
    border: "1px solid #2a2a2a",
    borderRadius: "10px",
    padding: "10px",
    zIndex: 9999,
    boxShadow: "0px 8px 20px rgba(0,0,0,0.45)"
  },

  tooltipTitle: {
    fontSize: "11px",
    fontWeight: "bold",
    color: "#fff",
    marginBottom: "6px"
  },

  tooltipBody: {
    fontSize: "11px",
    color: "#cfcfcf",
    lineHeight: "1.35"
  }
};
